// src/redux/authSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AuthService from "../../services/api/authService";
import AppSecureStorage from "../../services/secureStorage";
import { toast } from "react-toastify";
import { message } from "antd";

const auth = new AuthService();
const storage = new AppSecureStorage();

const completeLogin = (response, { dispatch, navigate }) => {
  // Some endpoints (e.g. /auth/mfa/verify) return the user/token fields flat
  // on the response body instead of nested under `data`, unlike
  // /auth/google/login. Normalize both shapes here.
  const data = response?.data ?? response;
  storage.set("permissions", JSON.stringify(data.permissions));
  dispatch(setIsSidebarActive(true));
  dispatch(setRole(data.role));
  storage.set("token", data.accessToken);
  storage.set("tokenExpiry", data.expiry);
  storage.set("role", data.role);
  storage.set("domain", data.email);
  storage.set("id", data.id);
  storage.set("name", data.name);
  storage.set("orgId", data?.organisationId);
  storage.set("profilePicture", data?.picture);
  const permissions = { Chat: ["Get"] };
  const firstKey = Object.keys(permissions)[0];
  let role = storage.get("role");
  if (role === "superadmin") {
    navigate("/analytics");
    window.location.replace("/analytics");
  } else {
    dispatch(setIsSidebarActive(true));
    navigate(`/${firstKey.toLowerCase()}`, { replace: true });
    window.location.replace(`/${firstKey.toLowerCase()}`);
  }
  dispatch(setIsSidebarActive(true));
  dispatch(setLoading(false));
};

export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async ({ data, navigate }, { dispatch }) => {
    dispatch(setLoading(true));
    const body = {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || data.clientId,
      credentials: data.credential,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    dispatch(setCredentials(body));
    try {
      dispatch(setIsSidebarActive(false));
      document.body.style.opacity = 1;
      const response = await auth.login(body);
      if (response?.data?.requiresMfa) {
        dispatch(setMfaRequired(response.data.mfaToken));
        dispatch(setLoading(false));
        return null;
      }
      if (response) {
        completeLogin(response, { dispatch, navigate });
        return response.data ?? response;
      } else {
        dispatch(setLoading(false));
        throw new Error();
      }
    } catch (error) {
      message.error(error?.data?.message ?? "Login failed. Please try again.");
      dispatch(setIsSidebarActive(false));
      dispatch(setLoading(false));
    }
  }
);

export const verifyMfaLogin = createAsyncThunk(
  "auth/verifyMfaLogin",
  async ({ code, navigate }, { dispatch, getState }) => {
    const { mfaToken } = getState().authReducer;
    dispatch(setLoading(true));
    try {
      const response = await auth.mfaVerifyLogin({ mfaToken, code });
      if (response) {
        dispatch(clearMfaRequired());
        completeLogin(response, { dispatch, navigate });
        return response.data ?? response;
      }
      dispatch(setLoading(false));
      throw new Error();
    } catch (error) {
      dispatch(setLoading(false));
      throw error;
    }
  }
);

export const refreshUserData = createAsyncThunk(
  "auth/refreshUserData",
  async ({ selectedRole }, { dispatch }) => {
    try {
      const response = await auth.getPermission();
      storage.set("role", selectedRole);
      storage.set("permissions", JSON.stringify(response));
      dispatch(setRole(selectedRole));
      return response.data;
    } catch (error) {
      toast.error("Failed to refresh user data. Please try again later.");
      throw error;
    }
  }
);
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    loading: "idle",
    clientId: null,
    credentials: null,
    role: null,
    loader: false,
    isSidebarActive: false,
    mfaRequired: false,
    mfaToken: null,
  },
  reducers: {
    setCredentials: (state, action) => {
      state.clientId = action.payload.clientId;
      state.credentials = action.payload.credentials;
    },
    setRole: (state, action) => {
      state.role = action.payload;
    },
    setLoading: (state, action) => {
      state.loader = action.payload;
    },
    setIsSidebarActive: (state, action) => {
      state.isSidebarActive = action.payload;
    },
    setMfaRequired: (state, action) => {
      state.mfaRequired = true;
      state.mfaToken = action.payload;
    },
    clearMfaRequired: (state) => {
      state.mfaRequired = false;
      state.mfaToken = null;
    },

    extraReducers: (builder) => {
      builder
        .addCase(googleLogin.pending, (state) => {
          state.loading = "loading";
        })
        .addCase(googleLogin.fulfilled, (state, action) => {
          state.loading = "succeeded";
          state.user = action.payload;
        })
        .addCase(googleLogin.rejected, (state) => {
          state.loading = "failed";
        });
    },
  },
});

export const {
  setCredentials,
  setRole,
  setLoading,
  setIsSidebarActive,
  setMfaRequired,
  clearMfaRequired,
} = authSlice.actions;

export default authSlice.reducer;
